/**
 * Deleting: the screen answers at once, the relay catches up behind it.
 *
 * A delete is a withdrawal the relay acts on — `nostr/withdrawn.ts` — and that
 * is a signature (an approval on a phone signer) and a relay round trip. A
 * person pressing Delete must not wait for either, so a delete is three steps
 * and only the first is in front of the person:
 *
 * 1. **Hide.** The stored message is marked `deleting`. Every list, count and
 *    search leaves it out from that write on, and so does a reload: the mark is
 *    on the record, so the queue survives the tab.
 * 2. **Withdraw**, a moment later, every hidden id in one request — a burst of
 *    deletes is one signature. The relay deletes the ciphertext.
 * 3. **Purge** the plaintext once the relay has the request. Not before: a
 *    delete that never reaches the relay has to be able to come back.
 *
 * A withdrawal that fails is tried again at the next sync. Failing twice, the
 * delete is abandoned and the message reappears, which is the honest answer:
 * nobody else's device would have hidden it. Attempts are counted per message,
 * so a delete made after a failure gets its own two tries.
 */
import type { RelayConnection } from "../nostr/relay";
import type { MailSigner } from "../nostr/signer";
import { fetchDeleted } from "../nostr/deleted";
import { fetchWithdrawn, publishWithdrawn } from "../nostr/withdrawn";

/** The local half of a delete. */
export interface DeleteStore {
  hide(id: string): Promise<void>;
  unhide(ids: readonly string[]): Promise<void>;
  /** Every message hidden and not yet withdrawn. */
  hidden(): Promise<string[]>;
  /** Drop the plaintext and remember the id, once the relay has it. */
  remove(id: string): Promise<void>;
  deletedIds(): Promise<string[]>;
  rememberDeleted(ids: readonly string[]): Promise<void>;
  purgeDeleted(ids: readonly string[]): Promise<number>;
  /** Whether this account's retired delete list has been withdrawn. */
  migrated(): Promise<boolean>;
  setMigrated(): Promise<void>;
}

export interface DeleteWiring {
  relay: RelayConnection;
  signer: MailSigner;
  /** The account whose mail this is. */
  pubkey: string;
  store: DeleteStore;
  /** The ids already deleted, read before the first render. */
  starting?: Iterable<string>;
  /** Told the ids changed, so a view can drop an open message. */
  onChanged?: () => void;
  /** Told a delete was given up and these messages are back. */
  onRestored?: (ids: readonly string[], error: unknown) => void;
  /** How long a delete waits for company before it is withdrawn. */
  settleMs?: number;
}

export type PublishResult = { ok: true } | { ok: false; error: unknown };

export interface Deleter {
  /** Is this wrap deleted, or on its way? A sync must not open it. */
  has(id: string): boolean;
  /** Hide one message now and queue its withdrawal. Resolves once it is hidden. */
  delete(id: string): Promise<void>;
  /** Withdraw whatever is queued, now. A sync calls this, which is the retry. */
  flush(): Promise<PublishResult>;
  /** Read what this account withdrew elsewhere, and drop that plaintext here. */
  readRemote(): Promise<void>;
}

/** The click's own withdrawal, then the next sync's. After that it comes back. */
export const PUBLISH_ATTEMPTS = 2;

export function createDeleter(w: DeleteWiring): Deleter {
  const gone = new Set<string>(w.starting ?? []);
  const queued = new Set<string>();
  const attempts = new Map<string, number>();
  let seenAt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // One withdrawal at a time, so a retry never races the request it retries.
  let chain: Promise<PublishResult> = Promise.resolve({ ok: true });

  const purge = async (ids: readonly string[]): Promise<void> => {
    // The relay already has these. A purge that fails here is retried on the
    // next flush and never counts against the delete.
    for (const id of ids) {
      try {
        await w.store.remove(id);
      } catch {
        /* still hidden; the next flush tries again */
      }
    }
  };

  const publishQueued = async (): Promise<PublishResult> => {
    const hiddenNow = await w.store.hidden();
    await purge(hiddenNow.filter((id) => gone.has(id)));
    const waiting = hiddenNow.filter((id) => !gone.has(id));
    if (waiting.length === 0) return { ok: true };

    try {
      await publishWithdrawn(w.relay, w.signer, waiting);
    } catch (error) {
      const abandoned = waiting.filter((id) => {
        const n = (attempts.get(id) ?? 0) + 1;
        attempts.set(id, n);
        return n >= PUBLISH_ATTEMPTS;
      });
      if (abandoned.length > 0) {
        for (const id of abandoned) {
          attempts.delete(id);
          queued.delete(id);
        }
        await w.store.unhide(abandoned);
        w.onRestored?.(abandoned, error);
      }
      return { ok: false, error };
    }

    for (const id of waiting) {
      gone.add(id);
      queued.delete(id);
      attempts.delete(id);
    }
    await w.store.rememberDeleted(waiting);
    await purge(waiting);
    w.onChanged?.();
    return { ok: true };
  };

  const flush = (): Promise<PublishResult> => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    chain = chain.then(publishQueued, publishQueued);
    return chain;
  };

  /**
   * Once per account: the retired list's ids become withdrawals, so the relay
   * deletes that mail too. Marked done only after it lands, so a failure is
   * retried at the next sync.
   */
  const migrate = async (): Promise<void> => {
    if (await w.store.migrated()) return;
    const legacy = await fetchDeleted(w.relay, w.pubkey, w.signer, 0);
    const ids = legacy?.list.ids ?? [];
    if (ids.length > 0) {
      await w.store.rememberDeleted(ids);
      for (const id of ids) gone.add(id);
      await publishWithdrawn(w.relay, w.signer, ids);
    }
    await w.store.setMigrated();
  };

  return {
    has: (id) => gone.has(id) || queued.has(id),

    async delete(id: string) {
      queued.add(id);
      await w.store.hide(id);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void flush(), w.settleMs ?? 800);
    },

    flush,

    async readRemote() {
      // A migration that fails must not stop mail arriving; it is retried.
      await migrate().catch(() => {});
      const { ids, newest } = await fetchWithdrawn(w.relay, w.pubkey, seenAt);
      seenAt = newest;
      if (ids.length === 0) return;
      await w.store.rememberDeleted(ids);
      for (const id of ids) gone.add(id);
      if (await w.store.purgeDeleted(ids)) w.onChanged?.();
    },
  };
}
